/*
 * TAB Vitamins quick cart: discount codes through the Ajax Cart API (/cart/update.js { discount }).
 *
 * Markup lives in snippets/tab-cart-summary.liquid. theme.dev.js replaces that block after every cart
 * change, so all listeners are delegated from document and state is re-applied on 'theme:cart:added'.
 */
(function () {
  const selectors = {
    summary: '[data-tab-cart-summary]',
    details: '[data-tab-discount-details]',
    input: '[data-tab-discount-input]',
    apply: '[data-tab-discount-apply]',
    remove: '[data-tab-discount-remove]',
    error: '[data-tab-discount-error]',
    total: '[data-tab-cart-total]',
    checkoutPrice: '[data-tab-checkout-price]',
    itemsQty: '[data-cart-items-qty]',
    drawer: '#cart-drawer',
  };

  let isPending = false;
  let isRestoring = false;

  // Keeps an open field and a half-typed code when the theme re-renders the summary (e.g. a quantity change)
  const draft = {open: false, value: ''};

  const updateUrl = `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart/update.js`;

  const sameCode = (a, b) => a.toLowerCase() === b.toLowerCase();

  function getAppliedCodes(summary) {
    return (summary.dataset.codes || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);
  }

  function setDiscountCodes(codes) {
    return fetch(updateUrl, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({discount: codes.join(',')}),
    }).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.json();
    });
  }

  function refreshCart() {
    if (window.cart && typeof window.cart.getCart === 'function') {
      window.cart.getCart();
    } else {
      // Cart page: the drawer instance isn't on window.cart there
      window.location.reload();
    }
  }

  function showError(summary, message) {
    const error = summary.querySelector(selectors.error);
    const input = summary.querySelector(selectors.input);

    if (error) {
      error.textContent = message;
      error.hidden = false;
    }

    if (input) {
      input.setAttribute('aria-invalid', 'true');
      input.focus();
    }
  }

  function clearError(summary) {
    const error = summary.querySelector(selectors.error);
    const input = summary.querySelector(selectors.input);

    if (error) {
      error.textContent = '';
      error.hidden = true;
    }

    if (input) input.removeAttribute('aria-invalid');
  }

  function setLoading(summary, isLoading) {
    const button = summary.querySelector(selectors.apply);
    if (!button) return;

    button.classList.toggle('is-loading', isLoading);
    button.setAttribute('aria-busy', String(isLoading));
  }

  function applyCode(summary) {
    const input = summary.querySelector(selectors.input);
    const code = input ? input.value.trim() : '';

    if (!code) {
      showError(summary, summary.dataset.errorEmpty);
      return;
    }

    if (isPending) return;
    isPending = true;
    clearError(summary);
    setLoading(summary, true);

    const existingCodes = getAppliedCodes(summary).filter((existing) => !sameCode(existing, code));

    setDiscountCodes([...existingCodes, code])
      .then((cart) => {
        const match = (cart.discount_codes || []).find((discount) => sameCode(discount.code, code));

        if (match && match.applicable) {
          draft.open = false;
          draft.value = '';
          refreshCart();
          return;
        }

        // Shopify keeps unusable codes on the cart; drop it again so it doesn't follow the customer to checkout
        return setDiscountCodes(existingCodes).then(() => showError(summary, summary.dataset.errorInvalid));
      })
      .catch(() => showError(summary, summary.dataset.errorGeneric))
      .finally(() => {
        isPending = false;
        setLoading(summary, false);
      });
  }

  function removeCode(summary, code) {
    if (isPending) return;
    isPending = true;

    const remainingCodes = getAppliedCodes(summary).filter((existing) => !sameCode(existing, code));

    setDiscountCodes(remainingCodes)
      .then(refreshCart)
      .catch(() => refreshCart())
      .finally(() => {
        isPending = false;
      });
  }

  // Mirror the summary total into the checkout button (the theme writes its own, currency-suffixed copy elsewhere)
  // and hide the item count pill once the cart is empty (the theme only updates its text)
  function syncDrawer() {
    const drawer = document.querySelector(selectors.drawer);
    if (!drawer) return;

    const total = drawer.querySelector(selectors.total);
    const checkoutPrice = drawer.querySelector(selectors.checkoutPrice);
    const itemsQty = drawer.querySelector(selectors.itemsQty);

    if (total && checkoutPrice) {
      checkoutPrice.textContent = total.textContent.trim();
    }

    if (itemsQty) {
      itemsQty.classList.toggle('hidden', parseInt(itemsQty.textContent, 10) === 0);
    }
  }

  function restoreDraft() {
    if (!draft.open && !draft.value) return;

    document.querySelectorAll(selectors.details).forEach((details) => {
      const input = details.querySelector(selectors.input);

      isRestoring = true;
      details.open = draft.open;
      if (input) input.value = draft.value;
      // The toggle event fires asynchronously
      setTimeout(() => {
        isRestoring = false;
      });
    });
  }

  document.addEventListener('click', (event) => {
    const applyButton = event.target.closest(selectors.apply);
    if (applyButton) {
      event.preventDefault();
      applyCode(applyButton.closest(selectors.summary));
      return;
    }

    const removeButton = event.target.closest(selectors.remove);
    if (removeButton) {
      event.preventDefault();
      removeCode(removeButton.closest(selectors.summary), removeButton.dataset.tabDiscountRemove);
    }
  });

  // The field sits inside the cart form: Enter must apply the code, not submit the form to checkout
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.matches(selectors.input)) return;

    event.preventDefault();
    applyCode(event.target.closest(selectors.summary));
  });

  document.addEventListener('input', (event) => {
    if (!event.target.matches(selectors.input)) return;

    draft.value = event.target.value;
    clearError(event.target.closest(selectors.summary));
  });

  // 'toggle' doesn't bubble, so listen in the capture phase
  document.addEventListener(
    'toggle',
    (event) => {
      if (!event.target.matches || !event.target.matches(selectors.details)) return;

      draft.open = event.target.open;

      if (event.target.open && !isRestoring) {
        const input = event.target.querySelector(selectors.input);
        if (input) input.focus();
      }
    },
    true
  );

  document.addEventListener('theme:cart:added', () => {
    syncDrawer();
    restoreDraft();
  });
})();
